import type { BattleUnit, Base } from '../BattleUnit.ts';
import type { CardOp } from '../cards/CardTypes.ts';
import { StateManager } from '../server/StateManager.ts';
import { randomSeed } from '../server/rng.ts';
import type {
  BattleEvent,
  SpellCommand,
  TickCommands,
  ValidationResult,
} from '../server/types.ts';
import type { ServerAdapter } from './ServerAdapter.ts';

/**
 * LocalServerAdapter simulates a server-authoritative connection
 * by running a local instance of StateManager.
 * It is used for single-player testing without requiring a real network layer.
 *
 * Phase 3c Step 5: methods are declared async to match the new
 * ServerAdapter interface, but they all resolve synchronously — the
 * StateManager call happens before the Promise is returned, so there
 * is effectively zero latency introduced.
 *
 * Phase 3d Step 3: room methods are no-op stubs. Single-player just
 * pretends it's already in a 1-player "room" with myTeam=0; the
 * Game.ts MENU "Single Player" path calls these without expecting
 * real pairing. There are no event subscribers in single-player.
 */
export class LocalServerAdapter implements ServerAdapter {
  private manager!: StateManager;
  /**
   * Card Tactician (M3) — set by `enableCardMode()` (called by
   * Game.startBattle for single-player). Applied to the StateManager in
   * `initBattle` so schedule validation uses the hand-driven card gate
   * instead of the三消 Points & Tiers gate. Client-only; NetworkServerAdapter
   * has no equivalent (PvP card validation = DeckMirror, M6).
   */
  private _cardMode = false;

  // -----------------------------------------------------------
  // Phase 3d — Room lifecycle (stubs for parity)
  // -----------------------------------------------------------

  async createRoom(): Promise<{ roomCode: string; myTeam: 0; terrainSeed: number }> {
    // Synthetic terrainSeed for single-mode parity. Game.ts doesn't
    // actually go through createRoom in single mode (it picks its own
    // seed in startSinglePlayer), but we honour the interface contract.
    return { roomCode: 'LOCAL0', myTeam: 0, terrainSeed: randomSeed() };
  }

  async joinRoom(_code: string): Promise<{ roomCode: string; myTeam: 1; terrainSeed: number }> {
    // Single-player has no real "join" path; this exists only to
    // satisfy the interface in case Game.ts calls it speculatively.
    return { roomCode: 'LOCAL0', myTeam: 1, terrainSeed: randomSeed() };
  }

  onEvent(_name: string, _handler: (data: any) => void): () => void {
    // Single-player never pushes events. Return an inert unsubscribe.
    return () => { /* noop */ };
  }

  // -----------------------------------------------------------
  // Battle session
  // -----------------------------------------------------------

  async initBattle(
    units: BattleUnit[],
    bases: Base[],
    seed: number,
    _boardSeed: number,
    terrainSeed: number,
    _deckId?: string,
    _deckSeed?: number,
  ): Promise<void> {
    // Single-player runs zero-latency against this local fake server; the
    // client already consumed the seed to produce `units`. We stash seed +
    // terrain so authoritative effect resolution agrees on positions.
    // `_boardSeed` / `_deckId` / `_deckSeed` are PvP-only (DeckMirror), ignored here.
    this.manager = new StateManager(units, bases);
    this.manager.state.seed = seed;
    this.manager.applyTerrainSeed(terrainSeed);
    if (this._cardMode) this.manager.setCardMode(true);
  }

  /**
   * Card Tactician (M3) hook — switch this local server into card-mode
   * schedule validation. Call BEFORE `initBattle` (Game.startBattle does,
   * right after constructing the adapter). Stashed as a flag and applied
   * when the StateManager is created in `initBattle`; also applied
   * immediately if the manager already exists.
   */
  enableCardMode(): void {
    this._cardMode = true;
    if (this.manager) this.manager.setCardMode(true);
  }

  /**
   * Card Tactician (M6) — no-op in single-player. There is no authoritative
   * server deck to keep in sync; the client already mutated its own local
   * deck (via `applyCardOp` in Game.ts). Exists only to satisfy the interface
   * (the network adapter forwards filter ops to the real server).
   */
  async cardOp(_team: 0 | 1, _op: CardOp): Promise<void> {
    /* no-op: single-player deck is client-managed */
  }

  async submitSchedule(
    team: 0 | 1,
    schedule: SpellCommand[],
  ): Promise<ValidationResult> {
    return this.manager.validateSchedule(team, schedule);
  }

  async executeExec(
    cmds: TickCommands,
  ): Promise<{ events: BattleEvent[]; winner: number }> {
    // In order for StateManager to allow executeExec, it needs to transition
    // from match3 -> sched -> exec.
    if (this.manager.state.phase === 'match3') {
      this.manager.enterScheduling();
    }
    // Execution will validate the schedule again inside.
    const result = this.manager.executeExec(cmds);
    // After EXEC finishes, if the game isn't over, StateManager doesn't automatically
    // transition back to match3, but in single-player, Game.ts resets its own phases.
    // For local simulation, we can just jump the phase back manually to simulate next round.
    if (result.winner === -1) {
      // simulate starting a new round
      this.manager.state.phase = 'match3';
      this.manager.state.playerStats = {};
      this.manager.state.aiStats = {};
      this.manager.state.playerUsedEls = [];
      this.manager.state.aiUsedEls = [];
    }
    return result;
  }
}
